/**
 * Moddex 2026 — Modern Theme Enhancements
 * Vanilla JS, no dependencies, safe to load deferred.
 */
(function () {
  'use strict';

  /* ── Tiny helpers ──────────────────────────────────────── */
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ══════════════════════════════════════════════════════════
     1. SCROLL PROGRESS BAR
     Thin coloured line at the very top that fills as you scroll.
  ══════════════════════════════════════════════════════════ */
  function initScrollProgress() {
    const bar = $('#scroll-progress-bar');
    if (!bar) return;

    function update() {
      const scrolled = window.scrollY;
      const total = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.width = (total > 0 ? Math.min(100, (scrolled / total) * 100) : 0) + '%';
    }

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ══════════════════════════════════════════════════════════
     2. STICKY HEADER — GLASS BLUR
     Adds .is-scrolled to <sticky-header> after 80px scroll.
  ══════════════════════════════════════════════════════════ */
  function initStickyHeaderGlass() {
    const header = $('sticky-header');
    if (!header) return;

    function toggle() {
      header.classList.toggle('is-scrolled', window.scrollY > 80);
    }

    window.addEventListener('scroll', toggle, { passive: true });
    toggle();
  }

  /* ══════════════════════════════════════════════════════════
     3. BACK TO TOP
     Float button that smoothly scrolls back up.
  ══════════════════════════════════════════════════════════ */
  function initBackToTop() {
    const btn = $('#back-to-top');
    if (!btn) return;

    function toggle() {
      btn.classList.toggle('is-visible', window.scrollY > 420);
    }

    window.addEventListener('scroll', toggle, { passive: true });
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
    toggle();
  }

  /* ══════════════════════════════════════════════════════════
     4. STICKY BUY BAR (product page)
     Slides up from the bottom when the real "Add to Cart"
     button scrolls out of view.
  ══════════════════════════════════════════════════════════ */
  function initStickyBuyBar() {
    const bar = $('#sticky-buy-bar');
    if (!bar) return;

    const sectionId = bar.dataset.sectionId;
    if (!sectionId) return;

    // Original form's submit button
    const originalForm = document.getElementById('product-form-' + sectionId);
    if (!originalForm) return;
    const originalBtn = originalForm.querySelector('[name="add"]');
    if (!originalBtn) return;

    // Wire the sticky button to click the real one
    const stickyBtn = bar.querySelector('.sticky-buy-bar__button');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        // Disable sticky btn briefly to prevent double-submit
        stickyBtn.disabled = true;
        originalBtn.click();
        setTimeout(() => { stickyBtn.disabled = false; }, 1500);
      });
    }

    // Show/hide bar based on original button visibility
    const observer = new IntersectionObserver(
      function (entries) {
        bar.classList.toggle('is-visible', !entries[0].isIntersecting);
      },
      { threshold: 0 }
    );
    observer.observe(originalBtn);

    // Keep sticky price in sync with variant changes
    // Dawn updates the price element in-DOM; watch for those changes.
    const stickyPrice = bar.querySelector('.sticky-buy-bar__price');
    if (stickyPrice) {
      const priceContainer = document.getElementById('price-' + sectionId);
      if (priceContainer) {
        new MutationObserver(function () {
          const live = priceContainer.querySelector('.price-item--sale, .price-item--regular');
          if (live) stickyPrice.textContent = live.textContent.trim();
        }).observe(priceContainer, { childList: true, subtree: true, characterData: true });
      }
    }
  }

  /* ══════════════════════════════════════════════════════════
     5. ANNOUNCEMENT BAR MARQUEE
     Scrolls long announcement text continuously.
     Only activates if the single-bar text is >38 chars.
  ══════════════════════════════════════════════════════════ */
  function initAnnouncementMarquee() {
    // Target single (non-slider) announcement
    const msgs = $$('.announcement-bar .announcement-bar__message');
    msgs.forEach(function (msg) {
      const span = msg.querySelector('span');
      if (!span) return;
      if (span.textContent.trim().length < 38) return;

      // Build two copies for seamless loop
      const copy1 = span.cloneNode(true);
      const copy2 = span.cloneNode(true);
      const track = document.createElement('div');
      track.className = 'marquee-track';
      track.appendChild(copy1);
      track.appendChild(copy2);

      msg.innerHTML = '';
      msg.appendChild(track);
      msg.classList.add('has-marquee');
    });
  }

  /* ══════════════════════════════════════════════════════════
     6. CART RECOMMENDATIONS
     Fetches "related" products via Shopify's API and injects
     them into #cart-upsell-grid when the cart drawer opens.
  ══════════════════════════════════════════════════════════ */
  function initCartRecommendations() {
    const container = $('#cart-recommendations');
    if (!container) return;

    let fetched = false;

    function moneyFmt(cents) {
      return '£' + (cents / 100).toFixed(2);
    }

    function renderRecs(products) {
      const grid = $('#cart-upsell-grid');
      if (!grid) return;
      const valid = products.filter(function (p) { return p.available; }).slice(0, 4);
      if (!valid.length) return;

      grid.innerHTML = valid.map(function (p) {
        const imgSrc = p.featured_image
          ? p.featured_image.replace(/(\.\w+)$/, '_160x160$1')
          : '';
        const imgTag = imgSrc
          ? '<img src="' + imgSrc + '" alt="' + p.title.replace(/"/g, '&quot;') + '" loading="lazy" width="72" height="72">'
          : '';
        return (
          '<a href="' + p.url + '" class="cart-upsell__item">' +
            imgTag +
            '<p class="cart-upsell__item-title">' + p.title + '</p>' +
            '<p class="cart-upsell__item-price">' + moneyFmt(p.price_min) + '</p>' +
          '</a>'
        );
      }).join('');

      container.style.display = 'block';
    }

    function fetchRecs() {
      if (fetched) return;
      const productId = container.dataset.productId;
      if (!productId || productId === 'nil') return;
      fetched = true;

      fetch('/recommendations/products.json?product_id=' + productId + '&limit=4&intent=related')
        .then(function (r) { return r.json(); })
        .then(function (d) { renderRecs(d.products || []); })
        .catch(function () { fetched = false; }); // allow retry on error
    }

    // Trigger on cart drawer open (Dawn dispatches a custom event)
    document.addEventListener('cart-drawer:open', fetchRecs);

    // Also observe the cart-drawer element for class changes
    const cartDrawer = $('cart-drawer');
    if (cartDrawer) {
      new MutationObserver(function () {
        if (cartDrawer.getAttribute('aria-hidden') === 'false' ||
            cartDrawer.classList.contains('active') ||
            cartDrawer.classList.contains('is-open')) {
          fetchRecs();
        }
      }).observe(cartDrawer, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });
    }

    // Fallback: fetch on first scroll (cart may have been opened before JS ran)
    window.addEventListener('scroll', function onScroll() {
      fetchRecs();
      window.removeEventListener('scroll', onScroll);
    }, { passive: true, once: true });
  }

  /* ══════════════════════════════════════════════════════════
     7. RECENTLY VIEWED PRODUCTS
     Saves the current product to localStorage on product pages.
     Renders a grid of up to 4 previously viewed products into
     #recently-viewed-container on any page.
  ══════════════════════════════════════════════════════════ */
  function initRecentlyViewed() {
    var KEY = 'mdx_rv';
    var MAX = 8;

    // — Save current product (product pages only) —
    var productInfo = $('product-info');
    if (productInfo) {
      var productId = productInfo.dataset.productId;
      if (productId) {
        var titleEl = productInfo.querySelector('h1');
        var priceEl = productInfo.querySelector('.price-item--sale, .price-item--regular');
        var imgEl   = productInfo.querySelector('.product__media-wrapper img[src]');

        var entry = {
          id:    productId,
          title: titleEl  ? titleEl.textContent.trim()  : '',
          price: priceEl  ? priceEl.textContent.trim()   : '',
          image: imgEl    ? imgEl.src                    : '',
          url:   window.location.pathname
        };

        try {
          var list = JSON.parse(localStorage.getItem(KEY) || '[]');
          list = list.filter(function (p) { return p.id !== productId; });
          list.unshift(entry);
          localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
        } catch (e) {}
      }
    }

    // — Render recently viewed strip —
    var container = $('#recently-viewed-container');
    if (!container) return;

    try {
      var saved = JSON.parse(localStorage.getItem(KEY) || '[]');
      var currentId = productInfo && productInfo.dataset.productId;
      var toShow = saved
        .filter(function (p) { return p.id !== currentId && p.url && p.title; })
        .slice(0, 4);

      if (!toShow.length) return;

      container.innerHTML =
        '<div class="recently-viewed page-width">' +
          '<h2 class="recently-viewed__heading title">Recently Viewed</h2>' +
          '<div class="recently-viewed__grid">' +
            toShow.map(function (p) {
              var imgHtml = p.image
                ? '<div class="recently-viewed__image"><img src="' + p.image + '" alt="' + p.title.replace(/"/g, '&quot;') + '" loading="lazy"></div>'
                : '';
              return (
                '<a href="' + p.url + '" class="recently-viewed__item">' +
                  imgHtml +
                  '<p class="recently-viewed__title">' + p.title + '</p>' +
                  '<p class="recently-viewed__price">' + p.price + '</p>' +
                '</a>'
              );
            }).join('') +
          '</div>' +
        '</div>';
    } catch (e) {}
  }

  /* ══════════════════════════════════════════════════════════
     8. LOAD MORE (collection pages)
     Converts the "Next page" pagination link into a
     "Load More" button that appends products inline.
  ══════════════════════════════════════════════════════════ */
  function initLoadMore() {
    var grid = $('#product-grid');
    if (!grid) return;

    var pagination = $('.pagination');
    if (!pagination) return;

    // Find the next-page anchor inside pagination
    var nextLink = pagination.querySelector('a[href*="page="]');
    if (!nextLink) {
      // Try rel="next" as well
      nextLink = pagination.querySelector('a[rel="next"]') ||
                 document.querySelector('link[rel="next"]');
    }
    if (!nextLink) return;

    // Build the Load More button
    var btn = document.createElement('button');
    btn.id = 'load-more-btn';
    btn.className = 'button load-more-btn';
    btn.textContent = 'Load More';
    btn.dataset.href = nextLink.href;

    // Hide native pagination, show our button above it
    pagination.parentNode.insertBefore(btn, pagination);
    pagination.style.display = 'none';

    btn.addEventListener('click', function () {
      var href = btn.dataset.href;
      if (!href || btn.classList.contains('loading')) return;

      btn.classList.add('loading');
      btn.textContent = 'Loading\u2026';

      fetch(href)
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = doc.getElementById('product-grid');
          if (!newGrid) throw new Error('No grid in response');

          var items = newGrid.children;
          while (items.length) {
            var item = items[0];
            // Re-trigger scroll animations if present
            item.style.setProperty('--animation-order', grid.children.length + 1);
            grid.appendChild(item);
          }

          // Find next page link in fetched page
          var fetchedPagination = doc.querySelector('.pagination');
          var fetchedNext = fetchedPagination &&
            (fetchedPagination.querySelector('a[href*="page="]') ||
             fetchedPagination.querySelector('a[rel="next"]'));

          if (fetchedNext) {
            btn.dataset.href = fetchedNext.href;
            btn.classList.remove('loading');
            btn.textContent = 'Load More';
          } else {
            // No more pages
            btn.remove();
          }
        })
        .catch(function () {
          btn.classList.remove('loading');
          btn.textContent = 'Load More';
        });
    });
  }

  /* ══════════════════════════════════════════════════════════
     9. NUMBER COUNTER ANIMATION
     Any element with data-counter="true" will count up from
     0 to its text value when it enters the viewport.
     Usage: <span data-counter>10,000</span>
  ══════════════════════════════════════════════════════════ */
  function initCounters() {
    var elements = $$('[data-counter]');
    if (!elements.length) return;

    function animateCounter(el) {
      var raw = el.textContent.trim();
      var numeric = parseFloat(raw.replace(/[^0-9.]/g, ''));
      if (isNaN(numeric)) return;

      var prefix = raw.match(/^[^0-9]*/)[0];
      var suffix = raw.match(/[^0-9.]*$/)[0];
      var duration = 1400;
      var startTime = null;

      function step(timestamp) {
        if (!startTime) startTime = timestamp;
        var progress = Math.min((timestamp - startTime) / duration, 1);
        // Ease-out cubic
        var eased = 1 - Math.pow(1 - progress, 3);
        var current = Math.round(eased * numeric);
        el.textContent = prefix + current.toLocaleString() + suffix;
        if (progress < 1) requestAnimationFrame(step);
      }

      requestAnimationFrame(step);
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          animateCounter(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.6 });

    elements.forEach(function (el) { observer.observe(el); });
  }

  /* ══════════════════════════════════════════════════════════
     INIT — runs after DOM is ready
  ══════════════════════════════════════════════════════════ */
  ready(function () {
    initScrollProgress();
    initStickyHeaderGlass();
    initBackToTop();
    initStickyBuyBar();
    initAnnouncementMarquee();
    initCartRecommendations();
    initRecentlyViewed();
    initLoadMore();
    initCounters();
  });
})();
